import { useMutation } from '@tanstack/react-query';
import { Col, Row } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { CalculationSetupApis } from 'apis/CalculationSetupApis';
import { PlusIcon } from 'assets/svg';
import { Button, ButtonFilter } from 'components';
import ButtonExport from 'components/Button/ButtonExport';
import ButtonImport from 'components/Button/ButtonImport';
import { UploadModal } from 'components/UploadModal_V2';
import { useUser } from 'contexts/UserProvider';
import { useCallback, useEffect, useRef, useState } from 'react';
import { exportExcel_v2 } from 'utils/constants/exportExcel';
import { toastError, toastSuccess } from 'utils/constants/functions';
import { IMPORT_REWARD_CONDITION } from 'utils/constants/importId';
import TableRewardConditionTab from '../RewardConditionTab/TableRewardConditionTab';
import FilterRewardCondition from './FilterRewardCondition';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';

const RewardConditionTab = () => {
  const { user } = useUser();
  const childRef = useRef<any>(null);
  const [formTable] = useForm();
  const [isAdd, setIsAdd] = useState<boolean>(false);
  const [dataTable, setDataTable] = useState<any>({});
  const [indexHandle, setIndexHandle] = useState<number>(-1);
  const [isOpenImportModal, setIsOpenImportModal] = useState<boolean>(false);
  const [isOpenFilter, setIsOpenFilter] = useState<boolean>(false);
  const [filters, setFilters] = useState<any>({});
  const [jobTitles, setJobTitles] = useState<
    Array<{
      id: string;
      jobName: string;
    }>
  >([]);
  const isFirstRenderEdit = useRef(true);

  const mutateGetRewardCondition = useMutation({
    mutationFn: CalculationSetupApis.getListRewardCondition,
    onSuccess: (res, variables) => {
      const result = res?.data?.getRewardSetup || {};
      if (variables.type === 'export') {
        exportExcel_v2(
          variables.headerRow,
          result.items || [],
          'Điều kiện thưởng',
        );
      } else {
        setDataTable(result);
      }
    },
  });

  const handleCancel = useCallback(() => {
    setIsAdd(false);
    setIndexHandle(-1);
    formTable.resetFields();
  }, [formTable]);

  const handleCancelTable = useCallback(() => {
    if (isAdd) {
      setDataTable((pre: any) => ({
        ...pre,
        items: (pre?.items || []).slice(1),
      }));
    }
    handleCancel();
  }, [handleCancel, isAdd]);

  const {
    mutate: mutateCreateRewardCondition,
    isPending: isPendingCreateRewardCondition,
  } = useMutation({
    mutationFn: CalculationSetupApis.createRewardSetup,
    onSuccess: (res) => {
      if (res?.errors?.length > 0) {
        toastError('Tạo điều kiện thất bại');
        return;
      }

      toastSuccess('Tạo điều kiện thành công');
      refetchGetRewardCondition();
      handleCancelTable();
    },
  });

  const {
    mutate: mutateUpdateRewardCondition,
    isPending: isPendingUpdateRewardCondition,
  } = useMutation({
    mutationFn: CalculationSetupApis.updateRewardSetup,
    onSuccess: (res) => {
      if (res?.errors?.length > 0) {
        toastError('Tạo điều kiện thất bại');
        return;
      }

      toastSuccess('Cập nhật điều kiện thành công');
      refetchGetRewardCondition();
      handleCancelTable();
    },
  });

  const refetchGetRewardCondition = useCallback(
    async (
      page = 1,
      page_size = DEFAULT_PAGE_SIZE,
      isExport?: boolean,
      headerRow?: any,
    ) => {
      try {
        console.log('refetchGetRewardCondition', {
          page,
          page_size,
          isExport,
          headerRow,
          filters,
        });
        await mutateGetRewardCondition.mutateAsync({
          page: isExport ? 1 : page,
          page_size: isExport
            ? dataTable.pageInfo?.totalItems || 1000
            : page_size,
          ...(isExport && {
            headerRow,
            type: 'export',
          }),
          ...filters,
        });
      } catch (error) {
        console.log('mutateGetRewardCondition', error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataTable.pageInfo?.totalItems, filters],
  );

  useEffect(() => {
    // console.log('user', user);
    refetchGetRewardCondition();
  }, []);

  const mutateSaveRewardCondition = useMutation({
    mutationFn: CalculationSetupApis.saveRewardCondition,
    onSuccess: (res, variables) => {
      toastSuccess('Lưu thành công');
      refetchGetRewardCondition();
    },
    onError: (error) => {
      toastError('Lưu thất bại');
      console.log(error);
    },
  });

  const handleExportExcel = () => {
    if (childRef.current) {
      childRef.current.exportExcel();
    }
  };

  const handleAddRow = () => {
    setIsAdd(true);
    setIndexHandle(-1);
    setDataTable((pre: any) => ({
      ...pre,
      items: [{ id: Date.now() }].concat(pre.items),
    }));
  };

  const validateForm = (value: any) => {
    if (isAdd && !value.id) return toastError('Nhập mã điều kiện');

    if (isAdd && !value.type) return toastError('Chọn loại');

    if (isAdd && !value.supportYn) return toastError('Chọn loại hỗ trợ');

    if (
      isAdd &&
      (!value.groupRoleManagement || value.groupRoleManagement.length === 0)
    )
      return toastError('Chọn mã thưởng DMS');

    if (!value.value) return toastError('Nhập giá trị thực hiện');

    return true;
  };

  const handleSaveTable = () => {
    const isValid = validateForm(formTable.getFieldsValue());

    if (!isValid) return;

    const values = formTable.getFieldsValue();
    values.supportYn = values.supportYn === 'Y';
    values.groupRoleManagement = Array.isArray(values.groupRoleManagement)
      ? values.groupRoleManagement.join(';')
      : values.groupRoleManagement;
    values.groupRoleId = jobTitles.map((item) => item.id).join(';');
    values.groupRoleName = jobTitles.map((item) => item.jobName).join(';');

    if (isAdd) {
      values.valueType = '%';
      mutateCreateRewardCondition(values);
    } else {
      const data = {
        ...dataTable.items[indexHandle],
        ...values,
      };

      mutateUpdateRewardCondition({
        condition: data.condition,
        groupRoleId: data.groupRoleId,
        groupRoleManagement: data.groupRoleManagement,
        groupRoleName: data.groupRoleName,
        id: data.id,
        mainSetupId: data.mainSetupId || null,
        supportYn: data.supportYn,
        type: data.type,
        value: data.value,
        valueType: '%',
        note: data.note,
        maxCondition: data.maxCondition,
      });
    }
  };

  const handleEditRow = (index: number) => {
    isFirstRenderEdit.current = true;
    setIndexHandle(index);
    setIsAdd(false);
    const row = dataTable.items[index];
    formTable.setFieldsValue({
      ...row,
      supportYn: row.supportYn ? 'Y' : 'N',
      groupRoleManagement: row.groupRoleManagement
        ? (row.groupRoleManagement || '').split(';')
        : [],
    });

    const jobNames = (row.groupRoleName || '').split(';');

    setJobTitles(
      row.groupRoleId
        ? row.groupRoleId.split(';').map((id: string, index: number) => ({
            id,
            jobName: jobNames[index],
          }))
        : [],
    );
  };

  const handleImportData = useCallback(() => {
    setIsOpenImportModal(true);
  }, []);

  const handleOkFilter = (values: any) => {
    setFilters(values);
  };

  const handleCancelFilter = () => {
    setFilters({});
  };

  useEffect(() => {
    refetchGetRewardCondition(1, DEFAULT_PAGE_SIZE, false, filters);
  }, [filters, refetchGetRewardCondition]);

  return (
    <>
      <Row gutter={[8, 16]} align={'middle'} justify={'end'} className="mb-4">
        <Col>
          <ButtonFilter isOpen={isOpenFilter} setIsOpen={setIsOpenFilter} />
        </Col>
        <Col>
          <ButtonExport
            onClick={handleExportExcel}
            disabled={isAdd || indexHandle !== -1}
          />
        </Col>
        <Col>
          <ButtonImport
            disabled={
              indexHandle !== -1 ||
              isAdd ||
              !(user?.isAdmin || user?.isHO || user?.isNS)
            }
            onClick={handleImportData}
          />
        </Col>
        <Col>
          <Button
            title="Tạo điều kiện"
            iconBefore={<PlusIcon />}
            handleOnclick={handleAddRow}
            disabled={
              indexHandle !== -1 ||
              isAdd ||
              !(user?.isAdmin || user?.isHO || user?.isNS)
            }
          />
        </Col>
      </Row>
      <FilterRewardCondition
        isOpen={isOpenFilter}
        onFinish={handleOkFilter}
        onCancel={handleCancelFilter}
      />
      <TableRewardConditionTab
        jobTitles={jobTitles}
        setJobTitles={setJobTitles}
        isFirstRenderEdit={isFirstRenderEdit}
        ref={childRef}
        form={formTable}
        data={dataTable}
        isAdd={isAdd}
        // setIsAdd={setIsAdd}
        editIndex={indexHandle}
        // setIndexHandle={setIndexHandle}
        onCancel={handleCancelTable}
        onEdit={handleEditRow}
        onSave={handleSaveTable}
        loading={
          mutateGetRewardCondition.isPending ||
          isPendingCreateRewardCondition ||
          isPendingUpdateRewardCondition
        }
        refetch={refetchGetRewardCondition}
      />

      <UploadModal
        importId={IMPORT_REWARD_CONDITION}
        templateFileName="Template Điều kiện thưởng"
        open={isOpenImportModal}
        title={'Thêm mới kế hoạch bán hàng'}
        setOpen={setIsOpenImportModal}
        refetch={refetchGetRewardCondition}
      />
    </>
  );
};

export default RewardConditionTab;
